import { useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Chip, Alert, Stack } from '@mui/material';
import {
  Close as CloseIcon,
  Circle as CircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Terminal, type TerminalHandle } from '@/components/terminal/Terminal';
import { useTerminal } from '@/hooks/useTerminal';
import { useAgent } from '@/hooks/useAgents';

// Vertical space consumed by MainLayout above the page content area (px).
// AppBar toolbar spacer (64) + main content padding top (24) + bottom (24) = 112px.
const MAIN_LAYOUT_OFFSET = 112;

export const TerminalPage: React.FC = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const terminalRef = useRef<TerminalHandle>(null);

  const { data: agent, isLoading: agentLoading, isError: agentLoadError } = useAgent(agentId ?? '');

  const handleData = useCallback((data: string | Uint8Array) => {
    terminalRef.current?.write(data);
  }, []);

  const terminalEnabled = !!agentId && (!!agent || agentLoading);

  const { status, error, disconnect, restartShell, sendData, resize } = useTerminal({
    agentId: agentId ?? '',
    enabled: terminalEnabled,
    onData: handleData,
  });

  const handleTerminalData = useCallback(
    (data: string | Uint8Array) => {
      // xterm.onData always emits strings (keyboard input); Uint8Array path is never reached here
      if (typeof data === 'string') {
        sendData(data);
      }
    },
    [sendData]
  );

  const handleTerminalResize = useCallback(
    (cols: number, rows: number) => {
      resize(cols, rows);
    },
    [resize]
  );

  const handleDisconnect = useCallback(() => {
    disconnect();
    navigate('/agents');
  }, [disconnect, navigate]);

  useEffect(() => {
    if (!agentId) {
      navigate('/agents');
    }
  }, [agentId, navigate]);

  if (!agentId) {
    return null;
  }

  const getStatusColor = (): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'connected':
        return 'success';
      case 'connecting':
        return 'warning';
      case 'error':
        return 'error';
      case 'terminated':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'connected':
        return 'Conectado';
      case 'connecting':
        return 'Conectando...';
      case 'error':
        return 'Error';
      case 'terminated':
        return 'Shell terminada';
      default:
        return 'Desconectado';
    }
  };

  const agentInfo = agent
    ? `${agent.hostname || agent.agentId} • ${agent.os || '—'}${agent.arch ? ` ${agent.arch}` : ''}`
    : agentId;

  return (
    <Box
      sx={{
        // Explicit height so the flex chain down to xterm works.
        // MAIN_LAYOUT_OFFSET accounts for the MUI AppBar toolbar spacer (64px)
        // plus the main content area top + bottom padding (24px * 2 = 48px).
        height: `calc(100vh - ${MAIN_LAYOUT_OFFSET}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Paper
        sx={{
          p: 2,
          mb: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5" component="h1">
            Terminal Remoto
          </Typography>
          <Chip
            icon={<CircleIcon />}
            label={getStatusText()}
            color={getStatusColor()}
            size="small"
          />
        </Box>

        <Stack direction="row" spacing={1}>
          {status === 'terminated' && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={restartShell}
              size="small"
            >
              Reiniciar Shell
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            onClick={handleDisconnect}
          >
            Desconectar
          </Button>
        </Stack>
      </Paper>

      {/* Agent Info */}
      <Paper sx={{ p: 1.5, mb: 1, flexShrink: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Agente:
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {agentLoading ? 'Cargando...' : agentInfo}
          </Typography>
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 1, flexShrink: 0 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Agent not found / unavailable */}
      {agentLoadError && !agentLoading && (
        <Alert
          severity="warning"
          sx={{ mb: 1, flexShrink: 0 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/agents')}>
              Volver
            </Button>
          }
        >
          El agente ya no existe o no esta disponible en el backend. Selecciona uno activo desde la
          lista.
        </Alert>
      )}

      {/* Shell terminated banner */}
      {status === 'terminated' && !error && (
        <Alert severity="info" sx={{ mb: 1, flexShrink: 0 }}>
          La shell remota terminó. Hacé click en "Reiniciar Shell" para abrir una nueva sesión.
        </Alert>
      )}

      {/* Terminal — takes all remaining vertical space */}
      {!agentLoadError ? (
        <Paper
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: '#1e1e1e',
            minHeight: 0, // required for flex children to shrink below content height
          }}
        >
          <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <Terminal
              ref={terminalRef}
              onData={handleTerminalData}
              onResize={handleTerminalResize}
            />
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 2, flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Abri una terminal desde un agente conectado en la lista de agentes.
          </Typography>
        </Paper>
      )}

      {/* Help Text */}
      <Paper sx={{ p: 1, mt: 1, flexShrink: 0, bgcolor: 'action.hover' }}>
        <Typography variant="caption" color="text.secondary">
          Tip: Terminal conectada al agente remoto. Los comandos se ejecutan en el sistema del
          agente.
        </Typography>
      </Paper>
    </Box>
  );
};
